import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import IntegrationSettingsService from '@app/model/integration-settings/service/IntegrationSettingsService';
import { CompanyHotelIntegrations, Integration } from '@app/model/integration-settings/Integration';
import CompanyService from '@app/model/company/service/CompanyService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';

export const INTEGRATION_KEYS = {
	company: ['integrations', 'company'] as const,
	companyHotel: (companyHotelId: number) => ['integrations', 'companyHotel', companyHotelId] as const,
	runs: (integrationId: number) => ['integrations', 'runs', integrationId] as const,
};

export const useCompanySettings = () => useQuery({
	queryKey: ['companySettings'],
	queryFn: CompanyService.findCompanySettings,
});

export const useCompanyIntegrations = (enabled = true) => useQuery({
    enabled,
	queryKey: INTEGRATION_KEYS.company,
	queryFn: IntegrationSettingsService.findCompanyIntegrations,
});

export const useCompanyHotelIntegrations = (companyHotelId: number, enabled = true) => useQuery({
    enabled,
	queryKey: INTEGRATION_KEYS.companyHotel(companyHotelId),
	queryFn: () => IntegrationSettingsService.findCompanyHotelIntegrations(companyHotelId),
	refetchInterval: 15000,
});

export const useIntegrationRuns = (integrationId: number | null) => useQuery({
	queryKey: INTEGRATION_KEYS.runs(integrationId ?? 0),
	queryFn: () => IntegrationSettingsService.findIntegrationRuns(integrationId ?? 0),
	enabled: integrationId !== null,
});

// Put a changed Integration into the cached property page, and refresh the overview.
export const useStoreIntegration = () => {
	const queryClient = useQueryClient();
	return (integration: Integration) => {
		queryClient.setQueryData<CompanyHotelIntegrations>(INTEGRATION_KEYS.companyHotel(integration.companyHotelId), old => old === undefined
			? old
			: { ...old, integrations: old.integrations.map(i => i.type === integration.type ? integration : i) });
		void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.company });
		void queryClient.invalidateQueries({ queryKey: CompanyHotelQueryKeys.all });
	};
};

export const useRunIntegrationBackfill = (onError: (error: string) => void) => {
	const store = useStoreIntegration();
	return useMutation({
		mutationFn: IntegrationSettingsService.runIntegrationBackfill,
		onSuccess: store,
		onError,
	});
};

export const useSaveIntegrationReportRules = (onSuccess: (integration: Integration) => void, onError: (error: string) => void) => {
	const store = useStoreIntegration();
	return useMutation({
		mutationFn: IntegrationSettingsService.saveIntegrationReportRules,
		onSuccess: (saved: Integration) => { store(saved); onSuccess(saved); },
		onError,
	});
};

export const useSetIntegrationEnabled = (onError: (error: string) => void) => {
	const store = useStoreIntegration();
	return useMutation({
		mutationFn: IntegrationSettingsService.setIntegrationEnabled,
		onSuccess: store,
		onError,
	});
};

export const catalogueMutationKey = (integration: Integration) => ['catalogue-refresh', integration.companyHotelId, integration.provider] as const;
export const useCatalogueRefreshing = (integration: Integration) => useIsMutating({ mutationKey: catalogueMutationKey(integration) }) > 0;

export const useRefreshIntegrationRooms = (integration: Integration, onError: (error: string) => void) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: catalogueMutationKey(integration),
        mutationFn: () => IntegrationSettingsService.refreshIntegrationRooms(integration.id),
        // Refresh may change plan eligibility/inventory even when its response is lost. Re-read consumers too.
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.companyHotel(integration.companyHotelId) });
            void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.company });
            void queryClient.invalidateQueries({ queryKey: ['integrations', 'runs'] });
            void queryClient.invalidateQueries({ queryKey: PricingAutomationQueryKeys.pricingAutomation });
            void queryClient.invalidateQueries({ queryKey: PricingAutomationQueryKeys.pmsMetrics(integration.companyHotelId) });
            void queryClient.invalidateQueries({ queryKey: CompanyHotelQueryKeys.all });
        },
        onError,
    });
};
